import { useState } from 'react';

import { IconInfoCircleOutline } from '@iwoca/icons';
import { Label, Link, Pill, TextArea } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import styles from './CompanyQuestionAI.module.css';
import {
  TCompanyQuestionRequest,
  useCompanyQuestionMutation,
} from '../../api/apiCompanyQuestion/apiCompanyQuestionMutation';
import { useGetApplicationQuery } from '../../api/lendingApi/lendingApiQueries';
import { useSignupRouterUK } from '../../hooks/useSignupRouter/useSignupRouterUK';
import { Button } from '../Button/Button';

type TCompanyQuestionAIData = {
  answer: string;
  sicCode?: number;
};

const normaliseCompanyQuestion = (
  data: TCompanyQuestionAIData,
): TCompanyQuestionRequest => {
  return {
    context: {
      sic_code: [],
    },
    history: [
      {
        question: 'what does your company do?',
        answer: data.answer,
      },
    ],
  };
};

const CompanyQuestionAI = () => {
  const [textCount, setTextCount] = useState(0);
  const [hasCompletedQuestions, setHasCompletedQuestions] = useState(false);

  const methods = useForm<TCompanyQuestionAIData>();
  const { application } = useGetApplicationQuery();
  const { submitCompanyQuestion, isLoading } = useCompanyQuestionMutation();

  const { navigateSignup } = useSignupRouterUK();

  const { handleSubmit, register } = methods;

  // TODO: normalise company information (mainly for SIC Code)

  const onSubmit = async (data: TCompanyQuestionAIData) => {
    if (!application?.application_id) {
      return;
    }

    try {
      const response = await submitCompanyQuestion({
        applicationId: application.application_id,
        data: normaliseCompanyQuestion(data),
      });

      if (!response) throw new Error('No questions populated');

      setHasCompletedQuestions(response.questions_complete);
      // TODO: Tracking amplitude page needed - leaving this here as an example
      // TODO: Page navigation logic
      if (response.questions_complete) {
        // TODO: @w.tong - verifying with design whether our intended UX is auto-redirect or manual CTA route. Currently set the behaviour to auto-redirect when complete.
        await navigateSignup({ route: 'check-eligibility' });
      }
    } catch (e) {
      throw new Error('Failed fetch', e as Error);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <Pill>Powered by AI</Pill>
        <h2 className={styles.heading}>
          Tell us a bit more about your business
        </h2>
        <p className={styles.description}>
          This will help us understand your business better and more accurately
          assess your application.
        </p>
        <div className={styles.info}>
          <IconInfoCircleOutline
            data-testid="infoIcon"
            width={16}
            height={16}
            className={styles.infoIcon}
          />
          <p className={styles.infoDescription}>
            There are a maximum of five AI generated questions and they should
            take around two minutes to complete.
          </p>
        </div>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <div className={styles.formFieldContainer}>
              <div className={styles.formField}>
                <Label htmlFor="aiQuestion1">What does your business do?</Label>
                <TextArea
                  id="aiQuestion1"
                  placeholder="Type your answer here..."
                  maxLength={300}
                  className={styles.questionTextArea}
                  {...register('answer', { required: true })}
                  onChange={(e) => setTextCount(e.currentTarget.value.length)}
                />
              </div>
              <p
                data-testid="character-count"
                className={styles.characterCount}
              >
                {textCount} / 300 characters
              </p>
            </div>
            <Button
              className={styles.nextQuestionBtn}
              type="submit"
              disabled={isLoading}
              loading={isLoading}
            >
              {hasCompletedQuestions
                ? 'Continue to check eligibility'
                : 'Continue to next question'}
            </Button>
            <p data-testid="ai-privacy-policy" className={styles.privacyPolicy}>
              By clicking “Continue” you agree to our{' '}
              <Link
                href="https://www.iwoca.co.uk/compliance/customer-privacy-policy"
                className={styles.privacyPolicyLink}
              >
                privacy policy
              </Link>
              .
            </p>
          </form>
        </FormProvider>
      </div>
    </>
  );
};

export default CompanyQuestionAI;
